import { AdminContentHeader } from '../hoc/Admin/AdminContentHeader';
import { useParams } from 'react-router-dom';
import {
  BrokerNerdsBarkerCoreEnumsUserAuthType,
  BrokerNerdsBarkerCoreEnumsUserRole,
  getGetApiTenantsTenantIdInvitesQueryKey,
  getGetApiTenantsTenantIdUsersQueryKey,
  useDeleteApiTenantsTenantIdInvitesInviteId,
  useDeleteApiTenantsTenantIdUsersUserId,
  useGetApiTenantsTenantIdInvites,
  useGetApiTenantsTenantIdUsers,
  usePostApiTenantsTenantIdInvites,
  usePutApiTenantsTenantIdUsersUserId,
} from '../api';
import { BNButton } from '../components/Button/Button';
import { Badge, Box, Group, Modal, Stack, Table, Text, Tooltip } from '@mantine/core';
import classes from './Companies.tsx.module.css';
import { useMemo, useState } from 'react';
import { openConfirmModal } from '@mantine/modals';
import { useGlobalState } from '../data/Global.state';
import { useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { BNTextInput } from '../components/TextInput/TextInput';
import { BNSelect } from '../components/Select/Select';
import { useForm } from '@mantine/form';
import GoogleIcon from '../components/icons/Google';

const USER_ROLE_LABELS: Partial<Record<BrokerNerdsBarkerCoreEnumsUserRole, string>> = {
  TenantAdmin: 'Company Admin',
  ReadOnlyUser: 'Read Only',
};

function getUserRoleLabel(roleId: BrokerNerdsBarkerCoreEnumsUserRole) {
  return USER_ROLE_LABELS[roleId] ?? roleId;
}

const inviteRoleOptions = [
  BrokerNerdsBarkerCoreEnumsUserRole.TenantAdmin,
  BrokerNerdsBarkerCoreEnumsUserRole.User,
  BrokerNerdsBarkerCoreEnumsUserRole.Accounting,
  BrokerNerdsBarkerCoreEnumsUserRole.ReadOnlyUser,
].map((roleId) => ({
  value: roleId,
  label: getUserRoleLabel(roleId),
}));

const editableRoleOptions = Object.values(BrokerNerdsBarkerCoreEnumsUserRole)
  .filter((roleId) => roleId !== BrokerNerdsBarkerCoreEnumsUserRole.GlobalAdmin && roleId !== BrokerNerdsBarkerCoreEnumsUserRole.None)
  .map((roleId) => ({
    value: roleId,
    label: getUserRoleLabel(roleId),
  }));

const roleColumnWidth = 300;

export function IsUserHidden(email: string, roleId: BrokerNerdsBarkerCoreEnumsUserRole) {
  return email.includes('brokernerds.com') || roleId === 'GlobalAdmin';
}

export default function Users() {
  const { tenantId } = useParams();
  const queryClient = useQueryClient();
  const { currentUser } = useGlobalState('currentUser');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<BrokerNerdsBarkerCoreEnumsUserRole | null>(null);
  const { data: users } = useGetApiTenantsTenantIdUsers(tenantId ?? '', {
    query: {
      enabled: !!tenantId,
      select: (data) => data.data,
    },
  });

  const { data: invites } = useGetApiTenantsTenantIdInvites(tenantId ?? '', {
    query: {
      enabled: !!tenantId,
      select: (data) => data.data,
    },
  });

  const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteApiTenantsTenantIdUsersUserId();
  const { mutateAsync: updateUser, isPending: isUpdatingUser } = usePutApiTenantsTenantIdUsersUserId();

  const { mutateAsync: deleteInvite, isPending: isDeletingInvite } = useDeleteApiTenantsTenantIdInvitesInviteId();

  const isEditingUser = !!editingUserId;

  const rows = useMemo(
    () =>
      (users ?? [])
        .map((u) => ({
          email: u.email,
          authType: u.authType,
          inventoryFilters: u.inventoryFilters,
          principalId: u.principalId,
          receiveNotifications: u.receiveNotifications,
          name: `${u.givenName} ${u.familyName}`.trim(),
          roleId: u.roleId,
          inviteId: null as string | null,
        }))
        .concat(
          (invites ?? []).map((i) => ({
            email: i.email,
            authType: BrokerNerdsBarkerCoreEnumsUserAuthType.Native,
            inventoryFilters: undefined,
            principalId: i.inviteId,
            receiveNotifications: false,
            name: '',
            roleId: i.roleId,
            inviteId: i.inviteId,
          })),
        )
        .filter((u) => !IsUserHidden(u.email, u.roleId))
        ?.sort((a, b) => a.email.localeCompare(b.email))
        .map((user) => {
          const isDeleteDisabled =
            !currentUser ||
            user.email === currentUser?.email ||
            (currentUser.roleId !== 'TenantAdmin' && currentUser.roleId !== 'GlobalAdmin') ||
            IsUserHidden(user.email, user.roleId);
          const isEditDisabled = isDeleteDisabled || !!user.inviteId;
          const isCurrentUserEditing = editingUserId === user.principalId;

          return (
            <tr key={user.principalId} style={{ position: 'relative' }}>
              <td>{user.email}</td>
              <td>
                {user.authType === BrokerNerdsBarkerCoreEnumsUserAuthType.Google && (
                  <Tooltip label="Google Authenticated User" withArrow>
                    <Box h={18} w={18}>
                      <GoogleIcon size={18} />
                    </Box>
                  </Tooltip>
                )}
              </td>
              {user.inviteId ? (
                <td>
                  <Badge color="green">INVITED</Badge>
                </td>
              ) : (
                <td>{user.name}</td>
              )}
              <td
                style={{
                  width: roleColumnWidth,
                  minWidth: roleColumnWidth,
                  paddingLeft: isCurrentUserEditing ? 2 : undefined,
                  paddingRight: isCurrentUserEditing ? 2 : undefined,
                  paddingTop: isCurrentUserEditing ? '0.375rem' : undefined,
                  paddingBottom: isCurrentUserEditing ? '0.375rem' : undefined,
                }}
              >
                {isCurrentUserEditing ? (
                  <Group gap="xs" wrap="nowrap" align="center">
                    <BNSelect
                      data={editableRoleOptions}
                      value={editingRoleId}
                      onChange={(value) => setEditingRoleId(value as BrokerNerdsBarkerCoreEnumsUserRole)}
                      disabled={isUpdatingUser}
                      w={154}
                    />
                    <BNButton
                      size="xs"
                      variant="filled"
                      color="green"
                      loading={isUpdatingUser}
                      disabled={!editingRoleId}
                      onClick={async () => {
                        if (!editingRoleId) {
                          return;
                        }

                        await updateUser({
                          tenantId: tenantId ?? '',
                          userId: user.principalId,
                          data: {
                            inventoryFilters: user.inventoryFilters,
                            receiveNotifications: user.receiveNotifications,
                            roleId: editingRoleId,
                          },
                        });
                        await queryClient.invalidateQueries({ queryKey: getGetApiTenantsTenantIdUsersQueryKey(tenantId ?? '') });
                        setEditingUserId(null);
                        setEditingRoleId(null);
                      }}
                    >
                      Save
                    </BNButton>
                    <BNButton
                      size="xs"
                      variant="default"
                      disabled={isUpdatingUser}
                      onClick={() => {
                        setEditingUserId(null);
                        setEditingRoleId(null);
                      }}
                    >
                      Cancel
                    </BNButton>
                  </Group>
                ) : (
                  getUserRoleLabel(user.roleId)
                )}
              </td>
              <td style={{ textAlign: 'center' }}>
                <Tooltip label="Insufficient permissions" disabled={!isDeleteDisabled}>
                  <Group gap="xs" justify="center" wrap="nowrap">
                    <BNButton
                      size="xs"
                      variant="outline"
                      disabled={isEditDisabled || isEditingUser}
                      onClick={() => {
                        setEditingUserId(user.principalId);
                        setEditingRoleId(user.roleId);
                      }}
                    >
                      Edit
                    </BNButton>
                    <BNButton
                      size="xs"
                      variant="outline"
                      color="red"
                      disabled={isDeleteDisabled || isEditingUser}
                      onClick={() => {
                        if (user.inviteId) {
                          openConfirmModal({
                            title: 'Delete Invitation',
                            children: <Text size="sm">Are you sure you want to delete the invitation to {user.email}?</Text>,
                            labels: { confirm: 'Confirm', cancel: 'Cancel' },
                            confirmProps: {
                              className: 'confirmButton',
                              variant: 'filled',
                              color: 'red',
                              size: 'sm',
                              loading: isDeletingInvite,
                            },
                            cancelProps: {
                              className: 'cancelButton',
                              variant: 'default',
                              size: 'sm',
                              disabled: isDeletingInvite,
                            },
                            closeButtonProps: { size: 'md' },
                            onConfirm: async () => {
                              await deleteInvite({ tenantId: tenantId ?? '', inviteId: user.inviteId ?? '' });
                              await queryClient.invalidateQueries({ queryKey: getGetApiTenantsTenantIdInvitesQueryKey(tenantId ?? '') });
                            },
                          });
                        } else {
                          openConfirmModal({
                            title: 'Delete User',
                            children: <Text size="sm">Are you sure you want to delete the user: {user.email}?</Text>,
                            labels: { confirm: 'Confirm', cancel: 'Cancel' },
                            confirmProps: {
                              className: 'confirmButton',
                              variant: 'filled',
                              color: 'red',
                              size: 'sm',
                              loading: isDeletingUser,
                            },
                            cancelProps: {
                              className: 'cancelButton',
                              variant: 'default',
                              size: 'sm',
                              disabled: isDeletingUser,
                            },
                            closeButtonProps: { size: 'md' },
                            onConfirm: async () => {
                              await deleteUser({ tenantId: tenantId ?? '', userId: user.principalId });
                              await queryClient.invalidateQueries({ queryKey: getGetApiTenantsTenantIdUsersQueryKey(tenantId ?? '') });
                            },
                          });
                        }
                      }}
                    >
                      Delete
                    </BNButton>
                  </Group>
                </Tooltip>
              </td>
            </tr>
          );
        }),
    [
      users,
      invites,
      currentUser,
      editingRoleId,
      editingUserId,
      isDeletingUser,
      isDeletingInvite,
      isEditingUser,
      isUpdatingUser,
      deleteInvite,
      deleteUser,
      queryClient,
      tenantId,
      updateUser,
    ],
  );

  const [opened, handlers] = useDisclosure(false);

  if (!users || !tenantId || !currentUser) {
    return null;
  }

  return (
    <>
      <AdminContentHeader
        sticky
        title="Users"
        rightSection={
          <Tooltip label="Insufficient permissions" disabled={currentUser.roleId === 'TenantAdmin' || currentUser.roleId === 'GlobalAdmin'}>
            <BNButton size="xs" variant="default" onClick={handlers.open} disabled={currentUser.roleId !== 'TenantAdmin' && currentUser.roleId !== 'GlobalAdmin'}>
              Add New
            </BNButton>
          </Tooltip>
        }
      />
      <Table verticalSpacing="md" className={classes.adminTable} width="100%">
        <thead>
          <tr>
            <th style={{ paddingLeft: 10 }}>Email</th>
            <th />
            <th style={{ paddingLeft: 10 }}>Name</th>
            <th style={{ paddingLeft: 10, width: roleColumnWidth, minWidth: roleColumnWidth }}>Role</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
      <InviteUserModal opened={opened} onClose={handlers.close} tenantId={tenantId} />
    </>
  );
}

function InviteUserModal({ opened, onClose, tenantId }: { opened: boolean; onClose: () => void; tenantId: string }) {
  const { mutateAsync: inviteUser, isPending: isInviting } = usePostApiTenantsTenantIdInvites();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      email: '',
      givenName: '',
      familyName: '',
      roleId: BrokerNerdsBarkerCoreEnumsUserRole.User,
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.email) {
        errors.email = 'Email is required';
      }

      if (!values.email.includes('@')) {
        errors.email = 'Invalid email';
      }

      if (!values.givenName) {
        errors.givenName = 'First name is required';
      }

      if (!values.familyName) {
        errors.familyName = 'Last name is required';
      }

      return errors;
    },
  });

  function handleFormClose() {
    form.reset();
    onClose();
  }

  return (
    <Modal opened={opened} onClose={handleFormClose} title="Invite User" withCloseButton>
      <form
        onSubmit={form.onSubmit(async (values) => {
          await inviteUser({ tenantId, data: values });
          await queryClient.invalidateQueries({ queryKey: getGetApiTenantsTenantIdInvitesQueryKey(tenantId) });
          handleFormClose();
        })}
      >
        <Stack>
          <BNTextInput label="Email" {...form.getInputProps('email')} />
          <Group>
            <BNTextInput label="First Name" {...form.getInputProps('givenName')} />
            <BNTextInput label="Last Name" {...form.getInputProps('familyName')} />
          </Group>
          <BNSelect
            label="Role"
            {...form.getInputProps('roleId')}
            withinPortal
            data={inviteRoleOptions}
          />
          <Group style={{ justifyContent: 'right' }}>
            <BNButton onClick={handleFormClose} disabled={isInviting}>
              Cancel
            </BNButton>
            <BNButton variant="filled" color="green" type="submit" loading={isInviting}>
              Invite
            </BNButton>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
